import React, { useState, useEffect } from 'react';
import { RecommendedAccessories } from '@hpi-sox/hpstellar-core';

import { Helpers } from '../../../core/src/helpers';
import useCartState from '../../../hooks/useCartState';
import useProductPrice from '../../../hooks/useProductPrice';
import useProductImpressions from '../../../hooks/useProductImpressionsV2';
import useDeviceInfo from '../../../hooks/useDeviceInfo';
import { useAttachableProductProps } from '../../../hooks/useProductProps';
import { getIcon } from '../../../category-landing-page/components/shared';
import { getPriceMetricInput, formatMetricValue } from '../../../metrics/metrics-helpers';
import { useDynamicYieldSort } from '../../../hooks/useDynamicYieldRecs';
import { mapInstockProducts } from './util';

import useTemplateTheme from '../../../hooks/useTemplateTheme';

import './cross-sell-container.less';

const mapCategoryIcons = categories => {
    return categories.map(({ title, id, iconName }) => {
        return {
            title,
            id,
            icon: getIcon(id),
        };
    });
};

const mapAccessories = Helpers.memoize(
    (products, prices = {}, baseProduct, getAttachableProductProps, cartId, list, xsellMethod, classes, isBot) => {
        return mapInstockProducts(
            products,
            prices,
            (p, price) => {
                const product = getAttachableProductProps(p, price, undefined, undefined, {
                    xsellProdInfo: baseProduct && baseProduct.sku,
                    xsellMethod,
                    cartId,
                    list,
                    position: (p && p.position) || i + 1,
                });
                return {
                    ...product,
                    price: { ...product.price, mainPriceClassName: classes.finalPrice },
                    link: {
                        ...product.link,
                        className: classes.productLink,
                    },
                    offerMessageClassName: classes.offerMessage,
                };
            },
            isBot,
        );
    },
    (products, prices, baseProduct, getAttachableProductProps, cartId, list, xsellMethod, classes, isBot) => {
        // Include SKU paired with price availability to handle async price loading
        const productPriceKeys = (products || []).map(p => `${p?.sku}:${p?.sku in prices}`).join('-');
        return `${productPriceKeys}-${baseProduct?.sku}-${cartId}-${isBot}`;
    },
);

const onAdd = relatedProducts => sku => {
    const product = relatedProducts.filter(product => product.sku === sku)[0];
    product.onAttach();
};

const AccessoriesComponent = ({
    relatedProducts,
    categories,
    onCategoryChange,
    title,
    productInitial,
    selectedCat,
}) => {
    const [getAttachableProductProps] = useAttachableProductProps();
    const { sku } = productInitial || {};
    const { prices } = useProductPrice(relatedProducts, { returnEmptyPriceObj: false });
    const { cartId } = useCartState();
    const { isBot } = useDeviceInfo(['isBot']);
    const list = 'pdp';
    const selectedCategory = Array.isArray(categories) ? categories.find(cat => cat && cat.id === selectedCat) : null;
    const { title: selectedCatTitle } = selectedCategory || {};
    const xsellMethod = `pdp-accessories-${formatMetricValue(selectedCatTitle)}`;
    const relatedProductsWithAnalytics = useProductImpressions({
        products: relatedProducts,
        list,
        componentKey: 'pdpAccessories',
        key: xsellMethod,
        impressionKey: selectedCat,
        isEmpty: relatedProducts && relatedProducts.length === 0,
        trackMetrics: true,
        preventRepeatingImpressions: true,
        customPrices: getPriceMetricInput(relatedProducts, prices),
        xsellMethod,
        xsellProdInfo: sku,
    });

    const { css, recommendedAccessories } = useTemplateTheme(['recommendedAccessories']);
    const { hideIcons = false } = recommendedAccessories || {};
    const classes = {
        tabActive: `${css}-tab-active`,
        tab: `${css}-tab`,
        desktopTitle: `${css}-desktop-title`,
        accordionTitle: `${css}-accordion-title recc-accordion-title`,
        tabsDropdown: `${css}-dropdown`,
        finalPrice: `${css}-finalPrice`,
        offerMessage: `${css}-offerMessage`,
        productLink: `${css}-productLink`,
        tabsWrapper: 'tabs-wrapper',
    };

    if (!relatedProductsWithAnalytics || relatedProductsWithAnalytics.length < 1) {
        return null;
    }
    const productsLoading = !isBot && Object.keys(prices).length < relatedProductsWithAnalytics.length;
    const accessories = mapAccessories(
        relatedProductsWithAnalytics,
        prices,
        productInitial,
        getAttachableProductProps,
        cartId,
        list,
        xsellMethod,
        classes,
        isBot,
    );
    const { products: resortedAccessories } = useDynamicYieldSort(
        'ab:crossSellDYSort',
        `${selectedCatTitle}`,
        //wait till prices are loaded to sort
        productsLoading ? [] : accessories,
        10,
    );

    const translations = {
        buttons: { attach: 'add to cart' },
        title,
    };

    const cats = hideIcons ? categories : mapCategoryIcons(categories);
    const products = resortedAccessories.length > 0 ? resortedAccessories : accessories;
    return (
        <RecommendedAccessories
            translations={translations}
            categories={cats}
            onCategoryChange={onCategoryChange}
            onAdd={onAdd(accessories)}
            addToCardTimeout={3000}
            products={products}
            productsLoading={productsLoading}
            className={`pdp-recommended-accessories-section${css ? ` ${css}` : ''}`}
            classes={classes}
            titleTypographyProps={{ className: 'recommended-acc-title', variant: 'orionSubtitleLarge' }}
        />
    );
};

export default ({ id, pdpAccessories, productInitial }) => {
    const { categories, products = {}, title } = pdpAccessories || {};
    const [firstCat] = categories || [];
    const [selectCat, setSelectCat] = useState(firstCat ? firstCat.id : null);

    useEffect(() => {
        if (categories && categories[0]) {
            setSelectCat(categories[0].id);
        }
    }, [categories]);
    if (!pdpAccessories || Object.keys(products).length < 1) {
        return null;
    }

    return (
        <section id={id}>
            <AccessoriesComponent
                title={title}
                relatedProducts={products[selectCat]}
                categories={categories}
                onCategoryChange={(index, id) => {
                    setSelectCat(id);
                }}
                productInitial={productInitial}
                selectedCat={selectCat}
            />
        </section>
    );
};
